import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import OrderFullscreenModal from './common/OrderFullscreenModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ApprovalRejectReasonModal from '../../../../components/modal/ApprovalRejectReasonModal';
import Message from '../../../../components/Message';
import { useOrderRemoteCollaboration } from '../../../../hooks/order/useOrderRemoteCollaboration';
import { useOrderContext } from '../../../../providers/OrderProvider';
import { useGetApprovalByIdQuery, useUpdateOrderApprovalByIdMutation } from '../../../../slices/approvalsApiSlice';
import { useNotifyOrderApprovedToApplicantMutation } from '../../../../slices/notificationsApiSlice';
import { markLocalOrderMutation } from '../../../../utils/order/orderLocalMutationGuard';
import { getRejectedOrderEditPath } from '../../../../utils/order/orderListPath';
import OrderFullscreenModalBody from './common/OrderFullscreenModalBody';
import OrderModalCollaborationNotice from './common/OrderModalCollaborationNotice';
import { OrderCollaborationLockContext } from './common/OrderCollaborationLockContext';
import OrderModalFooter from './common/OrderModalFooter';
import OrderModalHeader from './common/OrderModalHeader';
import OrderDetailModalBody from './common/OrderDetailModalBody';
import { hasPermission, PERM } from '../../../../utils/accessPermissions';

const ApproveModal = ({ setIsOpenPendingModal, isOpenPendingModal, refetchMyApprovals, onApprovalResolved, onRequestWithdraw, allowDecision = true }) => {
	const { userInfo } = useSelector((state) => state.auth);

	const { order, isLoading, isError, orderDetailsError, isOrderReady, currentOrderOId, refetch, refetchProcessingOrderLists } =
		useOrderContext();
	const { orderId, customerInfo, approval, approvalStatus, isCompleted } = order ?? {};
	const approvalId = approval && typeof approval === 'object' ? String(approval._id || '') : approval ? String(approval) : '';
	const orderOid = order?._id ? String(order._id) : '';
	const hasOrderForCurrent = Boolean(order?._id && currentOrderOId && String(order._id) === String(currentOrderOId));
	const showLoadError = isError && !hasOrderForCurrent;
	const loadErrorMessage =
		orderDetailsError?.data?.message || orderDetailsError?.error || '无法加载订单详情，您可能无权查看该订单。';

	// 重提后 _id 不变，打开弹窗强制拉最新详情，避免仍显示驳回缓存
	useEffect(() => {
		if (!isOpenPendingModal || !orderOid) return;
		if (typeof refetch === 'function') {
			void refetch();
		}
	}, [isOpenPendingModal, orderOid, refetch]);

	const { data: approvalDetail, refetch: refetchApprovalDetail } = useGetApprovalByIdQuery(approvalId, {
		skip: !isOpenPendingModal || !approvalId,
	});
	const approvalReason =
		String(approvalDetail?.reason || '').trim() ||
		(approval && typeof approval === 'object' ? String(approval.reason || '').trim() : '');
	const approvalDetailPayload =
		(approvalDetail?.detail && typeof approvalDetail.detail === 'object' ? approvalDetail.detail : null) ||
		(approval && typeof approval === 'object' && approval.detail && typeof approval.detail === 'object' ? approval.detail : null);
	const [updateOrderApprovalStatus, { isLoading: updatingApproval }] = useUpdateOrderApprovalByIdMutation();
	const [notifyOrderApprovedToApplicant] = useNotifyOrderApprovedToApplicantMutation();

	const [isPassing, setIsPassing] = useState(false);
	const [isRejecting, setIsRejecting] = useState(false);
	const [showRejectReasonModal, setShowRejectReasonModal] = useState(false);

	const isLoadingAction = updatingApproval || isPassing || isRejecting;
	const approvalStillWaiting = approvalDetail?.status ? approvalDetail.status === 'waitingApproval' : true;
	const canApprove =
		allowDecision &&
		hasPermission(userInfo, PERM.APPROVAL_ORDER_EXECUTE) &&
		order?.approvalStatus === 'inProcessOfApproval' &&
		Boolean(approvalId) &&
		approvalStillWaiting;

	const approveStatusBadge = useMemo(() => order?.uiHints?.modalStatusBadges?.default ?? null, [order]);

	const { observerNotice, observerTone, observerIcon, collaborationLocked, isRemoteSyncing, handleRemoteOrderUpdate } = useOrderRemoteCollaboration('approval', {
		enabled: isOpenPendingModal,
		isLocalAction: isLoadingAction,
		onBeforeRefetch: async () => {
			if (typeof refetchApprovalDetail === 'function') {
				return refetchApprovalDetail();
			}
			return undefined;
		},
	});

	const handleClose = useCallback(() => {
		setShowRejectReasonModal(false);
		setIsOpenPendingModal(false);
	}, [setIsOpenPendingModal]);

	const handlePass = useCallback(async () => {
		if (isPassing || isLoadingAction || !approvalId) return;
		setIsPassing(true);
		markLocalOrderMutation(order?._id);

		try {
			const approved_res = await updateOrderApprovalStatus({
				_id: approvalId,
				status: 'approved',
			}).unwrap();

			const { approver, applicant, assistant, detail, _id } = approved_res;
			const resolvedOrderId = detail?.orderId || orderId;

			try {
				const notificationPromises = [
					notifyOrderApprovedToApplicant({
						toUser: applicant,
						fromUser: approver,
						content: { message_text: `订单 ${resolvedOrderId} 已通过审批`, emoji: '✅', detail, approvalId: _id },
					}),
				];

				if (assistant) {
					notificationPromises.push(
						notifyOrderApprovedToApplicant({
							toUser: assistant,
							fromUser: approver,
							content: { message_text: `订单 ${resolvedOrderId} 已通过审批`, emoji: '✅', detail, approvalId: _id },
						}),
					);
				}

				await Promise.all(notificationPromises);
			} catch (notifyError) {
				console.error('审批通过通知发送失败:', notifyError);
			}

			toast.success(`#${resolvedOrderId} 已审批通过`);
			setIsOpenPendingModal(false);
			try {
				if (typeof onApprovalResolved === 'function') {
					onApprovalResolved({ orderOid, orderId: resolvedOrderId, result: 'approved' });
				} else {
					refetchProcessingOrderLists?.();
					refetchMyApprovals?.();
				}
			} catch {
				/* 列表刷新失败不影响已成功的审批 */
			}
		} catch (error) {
			toast.error(error?.data?.message || error?.error || '审批操作失败，请稍后重试');
		} finally {
			setTimeout(() => setIsPassing(false), 1500);
		}
	}, [
		isPassing,
		isLoadingAction,
		approvalId,
		orderOid,
		orderId,
		order?._id,
		updateOrderApprovalStatus,
		notifyOrderApprovedToApplicant,
		refetchMyApprovals,
		refetchProcessingOrderLists,
		onApprovalResolved,
		setIsOpenPendingModal,
	]);

	const handleReject = useCallback(
		async (rejectReason) => {
			if (isRejecting || isLoadingAction || !approvalId) return;
			const approvalSuggestions = String(rejectReason || '').trim();
			if (!approvalSuggestions) {
				toast.error('请填写驳回原因');
				return;
			}
			setIsRejecting(true);
			markLocalOrderMutation(order?._id);

			try {
				const rejected_res = await updateOrderApprovalStatus({
					_id: approvalId,
					status: 'reject',
					approvalSuggestions,
				}).unwrap();

				const { approver, applicant, assistant, detail, _id } = rejected_res;
				const resolvedOrderId = detail?.orderId || orderId;
				const rejectedEditPath = getRejectedOrderEditPath(detail?.orderOID || order?._id);

				try {
					const notificationPromises = [
						notifyOrderApprovedToApplicant({
							toUser: applicant,
							fromUser: approver,
							content: { message_text: `订单 ${resolvedOrderId} 未通过审批`, emoji: '❌', detail, approvalId: _id, editPath: rejectedEditPath },
						}),
					];

					if (assistant) {
						notificationPromises.push(
							notifyOrderApprovedToApplicant({
								toUser: assistant,
								fromUser: approver,
								content: { message_text: `订单 ${resolvedOrderId} 未通过审批`, emoji: '❌', detail, approvalId: _id, editPath: rejectedEditPath },
							}),
						);
					}

					await Promise.all(notificationPromises);
				} catch (notifyError) {
					console.error('审批驳回通知发送失败:', notifyError);
				}

				toast.success(`#${resolvedOrderId} 已驳回`);
				setShowRejectReasonModal(false);
				setIsOpenPendingModal(false);
				try {
					if (typeof onApprovalResolved === 'function') {
						onApprovalResolved({ orderOid, orderId: resolvedOrderId, result: 'reject' });
					} else {
						refetchProcessingOrderLists?.();
						refetchMyApprovals?.();
					}
				} catch {
					/* 列表刷新失败不影响已成功的驳回 */
				}
			} catch (error) {
				toast.error(error?.data?.message || error?.error || '驳回操作失败，请稍后重试');
			} finally {
				setTimeout(() => setIsRejecting(false), 1500);
			}
		},
		[
			isRejecting,
			isLoadingAction,
			approvalId,
			orderOid,
			orderId,
			order?._id,
			updateOrderApprovalStatus,
			notifyOrderApprovedToApplicant,
			refetchMyApprovals,
			refetchProcessingOrderLists,
			onApprovalResolved,
			setIsOpenPendingModal,
		],
	);

	return (
		<OrderFullscreenModal show={isOpenPendingModal} onHide={handleClose}>
			<OrderModalHeader
				orderId={orderId}
				customerInfo={customerInfo}
				isLoading={isLoading}
				isCompleted={isCompleted}
				approvalStatus={approvalStatus}
				badges={approveStatusBadge ? [approveStatusBadge] : []}
				autoAddPendingApprovalBadge={false}
			/>
			<OrderFullscreenModalBody isLoading={isLoading}>
				{showLoadError ? (
					<Message variant='danger' className='my-4'>
						{loadErrorMessage}
					</Message>
				) : (
					<OrderCollaborationLockContext.Provider value={collaborationLocked}>
						<OrderModalCollaborationNotice message={observerNotice} tone={observerTone} icon={observerIcon} isSyncing={isRemoteSyncing} />
						<OrderDetailModalBody
							notesType='approval'
							pendingFocus='approval'
							approvalReason={approvalReason}
							approvalDetail={approvalDetailPayload}
							contractTooltipId='approve-contract-toggle-tooltip'
							invoiceTooltipId='approve-invoice-toggle-tooltip'
						/>
					</OrderCollaborationLockContext.Provider>
				)}
			</OrderFullscreenModalBody>
			<OrderModalFooter onRemoteOrderUpdate={handleRemoteOrderUpdate}>
				{onRequestWithdraw ? (
					<Button variant='primary' size='sm' onClick={onRequestWithdraw} disabled={!isOrderReady || isLoadingAction || collaborationLocked}>
						<FontAwesomeIcon icon='undo' className='fs-11 me-2' />
						撤回
					</Button>
				) : null}
				{allowDecision && (canApprove || collaborationLocked) &&
					(isLoadingAction ? (
						<Spinner variant='primary' size='sm' />
					) : (
						<>
							<Button variant='success' onClick={handlePass} disabled={!isOrderReady || isPassing || collaborationLocked} size='sm'>
								{isPassing ? <Spinner size='sm' className='me-1' /> : <FontAwesomeIcon icon='check' className='me-1' />}
								同意
							</Button>
							<Button
								variant='danger'
								onClick={() => setShowRejectReasonModal(true)}
								disabled={!isOrderReady || isRejecting || collaborationLocked}
								size='sm'
							>
								{isRejecting ? <Spinner size='sm' className='me-1' /> : <FontAwesomeIcon icon='times' className='me-1' />}
								不同意
							</Button>
						</>
					))}
			</OrderModalFooter>
			<ApprovalRejectReasonModal
				show={showRejectReasonModal}
				onHide={() => setShowRejectReasonModal(false)}
				onConfirm={handleReject}
				confirmWaiting={isRejecting}
			/>
		</OrderFullscreenModal>
	);
};

export default ApproveModal;
